import React, { useEffect, useState } from "react";
import { Typography, Space, Tabs, Row, Col } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { CreditCardOutlined, DollarOutlined, HistoryOutlined, SettingOutlined } from "@ant-design/icons";
import BillingOverview from "./BillingOverview";
import BillingPaymentMethods from "./BillingPaymentMethods";
import BillingHistory from "./BillingHistory";
import BillingPreferences from "./BillingPreferences";

const { TabPane } = Tabs;

const BillingBase: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>("1");

  // Styles object to maintain consistent styling
  const styles = {
    contentCard: {
      background: "#fff",
      padding: "24px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      border: "1px solid #f0f0f0",
    },
    tabIcon: {
      marginRight: "8px",
    },
    subtitle: {
      fontSize: "14px",
      color: "#8c8c8c",
      display: "block",
      marginBottom: "8px",
    },
    tableContainer: {
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    },
  };

  // Map hash to tab keys
  const tabKeys: any = {
    overview: "1",
    paymentMethods: "2",
    history: "3",
    preferences: "4",
  };

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    setActiveTab(tabKeys[hash] || "1");
  }, [location]);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    navigate(`#${Object.keys(tabKeys).find(k => tabKeys[k] === key) || "overview"}`);
  };

  // Add global CSS for interactive rows
  useEffect(() => {
    // Add styles for interactive rows
    const style = document.createElement('style');
    style.innerHTML = `
      .interactive-row:hover td {
        background-color: #f5f8ff !important;
      }
      .details-button:hover {
        background-color: #f0f7ff !important;
        box-shadow: 0 2px 4px rgba(24, 144, 255, 0.1) !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="billing-container" style={{ padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: "32px" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space direction="vertical" size={4}>
              <Typography.Title level={2} style={{ margin: 0, fontWeight: 600 }}>
                Billing
              </Typography.Title>
              <Typography.Text type="secondary">
                Manage your payment methods, billing history, and preferences
              </Typography.Text>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Main Content */}
      <Tabs 
        activeKey={activeTab} 
        onChange={handleTabChange}
        type="card"
        className="custom-tabs"
        tabBarStyle={{ marginBottom: "24px" }}
        tabBarGutter={12}
        animated={{ inkBar: true, tabPane: true }}
      >
        <TabPane 
          tab={
            <span style={{ padding: "0 4px", display: "flex", alignItems: "center", gap: "8px" }}>
              <DollarOutlined style={styles.tabIcon} />
              Overview
            </span>
          }
          key="1"
        >
          <div style={styles.contentCard}>
            <BillingOverview onTabChange={handleTabChange} />
          </div>
        </TabPane>
        
        <TabPane 
          tab={
            <span style={{ padding: "0 4px", display: "flex", alignItems: "center", gap: "8px" }}>
              <CreditCardOutlined style={styles.tabIcon} />
              Payment Methods
            </span>
          }
          key="2"
        >
          <div style={styles.contentCard}>
            <BillingPaymentMethods />
          </div>
        </TabPane>
        
        <TabPane 
          tab={
            <span style={{ padding: "0 4px", display: "flex", alignItems: "center", gap: "8px" }}>
              <HistoryOutlined style={styles.tabIcon} />
              Billing History
            </span>
          }
          key="3"
        >
          <div style={styles.contentCard}>
            <BillingHistory />
          </div>
        </TabPane>
        
        <TabPane 
          tab={
            <span style={{ padding: "0 4px", display: "flex", alignItems: "center", gap: "8px" }}>
              <SettingOutlined style={styles.tabIcon} />
              Preferences
            </span>
          }
          key="4"
        >
          <div style={styles.contentCard}>
            <BillingPreferences />
          </div>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default BillingBase;
